'use client';

import { Button } from '@coas/ui/button';
import { cn } from '@coas/ui/lib';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';
import type { IndicatorsStatus, TriAction } from '../model/types';

export type CategoryTreeRowProps = {
    id: string;
    label: string;
    depth: number;
    hasChildren: boolean;
    isExpanded: boolean;
    status: IndicatorsStatus;
    action: TriAction;
    disabled?: boolean;
    onToggle?: (id: string) => void;
    onAction: (id: string, next: TriAction) => void;
    className?: string;
};

const statusDotClass: Record<IndicatorsStatus, string> = {
    all: 'bg-emerald-500',
    partial: 'bg-amber-500',
    none: 'bg-neutral-300',
};

function cycle(nextFrom: TriAction): TriAction {
    return nextFrom === 'none' ? 'add' : nextFrom === 'add' ? 'remove' : 'none';
}

const BaseRow = ({
    id,
    label,
    depth,
    hasChildren,
    isExpanded,
    status,
    action,
    disabled,
    onToggle,
    onAction,
    className,
}: CategoryTreeRowProps) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled === true) return;

        if (e.key === 'ArrowRight') {
            if (hasChildren && !isExpanded && onToggle) {
                e.preventDefault();
                onToggle(id);
            }
        } else if (e.key === 'ArrowLeft') {
            if (hasChildren && isExpanded && onToggle) {
                e.preventDefault();
                onToggle(id);
            }
        } else if (e.key === ' ') {
            e.preventDefault();
            onAction(id, cycle(action));
        }
    };

    const toggleBtn = hasChildren ? (
        <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={() => onToggle?.(id)}
            disabled={disabled === true}
            className={cn(
                'mr-1 size-5 rounded-4 text-neutral-500 transition-colors',
                'hover:bg-neutral-100 hover:text-neutral-700',
                'focus-visible:ring-2 focus-visible:ring-neutral-900/20',
            )}
            aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
            aria-expanded={isExpanded}
            aria-controls={`tree-children-${id}`}
        >
            {isExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
        </Button>
    ) : (
        <span className='mr-1 inline-block size-5' aria-hidden='true' />
    );

    return (
        <div
            role='treeitem'
            aria-expanded={hasChildren ? isExpanded : undefined}
            onKeyDown={handleKeyDown}
            className={cn(
                'group gap-2 flex min-h-8 items-center rounded-6 px-2',
                'outline-none transition-colors duration-150',
                'hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-neutral-900/20',
                className,
            )}
            style={{ paddingLeft: 8 + depth * 14 }}
            data-depth={depth}
        >
            {toggleBtn}

            <span className={cn('mr-2 inline-block size-2 rounded-full', statusDotClass[status])} />

            <span className='max-w-full truncate text-14 md:text-13 text-neutral-900' title={label}>
                {label}
            </span>

            {action !== 'none' && (
                <span
                    className={cn(
                        'ml-2 inline-flex items-center rounded-6 px-1.5 py-0.5 text-11 font-600 transition-colors duration-150',
                        action === 'add' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-700',
                    )}
                >
                    {action === 'add' ? 'Add' : 'Remove'}
                </span>
            )}

            <div className='ml-auto flex items-center gap-1'>
                <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => onAction(id, 'add')}
                    disabled={disabled === true}
                    className={cn(
                        'border-emerald-200 text-emerald-700 transition-colors duration-150',
                        'hover:bg-emerald-50',
                        action === 'add' && 'bg-emerald-100',
                    )}
                    title='Add'
                >
                    Add
                </Button>

                <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => onAction(id, 'remove')}
                    disabled={disabled === true}
                    className={cn(
                        'border-rose-200 text-rose-700 transition-colors duration-150',
                        'hover:bg-rose-50',
                        action === 'remove' && 'bg-rose-100',
                    )}
                    title='Remove'
                >
                    Remove
                </Button>

                <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => onAction(id, 'none')}
                    disabled={disabled === true}
                    className={cn(
                        'border-neutral-200 text-neutral-600 transition-colors duration-150',
                        'hover:bg-neutral-50',
                        action === 'none' && 'bg-neutral-100',
                    )}
                    title='Clear'
                >
                    Clear
                </Button>
            </div>
        </div>
    );
};

export const CategoryTreeRow = React.memo(BaseRow, (prev, next) => {
    return (
        prev.id === next.id &&
        prev.label === next.label &&
        prev.depth === next.depth &&
        prev.hasChildren === next.hasChildren &&
        prev.isExpanded === next.isExpanded &&
        prev.status === next.status &&
        prev.action === next.action &&
        prev.disabled === next.disabled
    );
});
